import { enumObject, EnumObjectValue } from '@app/base';
import { Role, Optional } from '@app/main/api';
import {
  EncounterExaminationHistoryItemFragment,
  EncounterListItemFragment,
  PreviousEncounterFragment,
  EncounterReportListItemFragment,
  GetEncounterOverviewQuery,
  GetEncounterQuery,
  GetPreviousEncounterQuery,
  GetEncounterReportQuery,
  PastEncounterSummaryFragment,
  UnbilledEncounterListItemFragment,
  EncounterSummaryHistoryItemFragment,
} from './graphql';
import { EyeHealthRequest } from './eyeHealthTypes';
import { GlaucomaTests } from './glaucomaTestsTypes';

export interface EncounterHistory {
  presentingComplaint: any | null;
  ocularHistory: any | null;
  generalHealth: any | null;
  familyHistory: any | null;
  medicationsAndAllergies: any | null;
  lifestyle: any | null;
  mobility: EncounterMobility | null;
  position: EncounterPosition | null;
  cooperation: EncounterCooperation | null;
}

// TODO: Temporary interface. Remove when SW-18 is implemented and use GraphQL Encounter Fragment instead.
export interface EncounterSummary {
  advice: any | null;
  visionStatus: any | null;
  eyeHealth: any | null;
  visionCorrection: any | null;
  givenPrescription: RefractionRequest | null;
  recall: number | null;
}

// TODO: Temporary interface. Remove when SW-18 is implemented and use GraphQL Encounter Fragment instead.
export interface EncounterExamination {
  investigation: InvestigationRequest | null;
  eyeHealth: EyeHealthRequest | null;
  supplementaryTests: EncounterSupplementaryTestsRequest | null;
  refractions: EncounterRefractionsRequest | null;
  ocularMetrics: EncounterOcularMetricsRequest | null;
}

export type UpdateEncounterHistoryRequest = EncounterHistory;

export interface TonometryEyeRequest {
  reading1: number | null;
  reading2: number | null;
  reading3: number | null;
  reading4: number | null;
  reading5: number | null;
  median: number | null;
}

export interface TonometryMeasurementRequest {
  time: string | null;
  leftEye: TonometryEyeRequest | null;
  rightEye: TonometryEyeRequest | null;
}

export interface EncounterTonometryRequest {
  instrument: TonometryInstrument | null;
  preIOP: TonometryMeasurementRequest | null;
  postIOP: TonometryMeasurementRequest | null;
}

export interface EncounterVisualFieldRequest {
  instrument: VisualFieldInstrument | null;
  note: string | null;
}

export interface EncounterPachymetryEyeRequest {
  reading1: EncounterPachymetryReadingRequest | null;
  reading2: EncounterPachymetryReadingRequest | null;
  reading3: EncounterPachymetryReadingRequest | null;
}

export interface EncounterPachymetryReadingRequest {
  cct: number | null;
  sd: number | null;
}

export interface EncounterPachymetryRequest {
  instrument: PachymetryInstrument | null;
  leftEye: EncounterPachymetryEyeRequest | null;
  rightEye: EncounterPachymetryEyeRequest | null;
}

export interface CoverTestFieldsRequest {
  distance: string | null;
  near: string | null;
}

export interface CoverTestRequest {
  withRx: CoverTestFieldsRequest | null;
  withoutRx: CoverTestFieldsRequest | null;
}

export interface EncounterSupplementaryTestsRequest {
  coverTest: CoverTestRequest | null;
  motility: string | null;
  accommodation: string | null;
  convergence: string | null;
  stereopsis: string | null;
  colourVision: ColourVision | null;
  additionalNotes: string | null;
}

export interface InvestigationRequest {
  tonometry: EncounterTonometryRequest | null;
  pachymetry: EncounterPachymetryRequest | null;
  visualField: EncounterVisualFieldRequest | null;
  fundusCamera: FundusCameraRequest | null;
}

export interface RefractionEyeRequest {
  uav: string | null;
  sphere: number | null;
  cylinder: number | null;
  axis: number | null;
  distPrism: string | null;
  va: string | null;
  add: number | null;
  nearPrism: string | null;
  nva: number | null;
}

export interface IntermediateRefractionEyeRequest {
  add: number | null;
  prism: string | null;
  va: string | null;
}

export interface IntermediateRefractionRequest {
  leftEye: IntermediateRefractionEyeRequest | null;
  rightEye: IntermediateRefractionEyeRequest | null;
}

export interface ObjectiveRefractionEyeRequest {
  sphere: number | null;
  cylinder: number | null;
  axis: number | null;
  va: string | null;
}

export interface RefractionRequest {
  leftEye: RefractionEyeRequest | null;
  rightEye: RefractionEyeRequest | null;
  intermediate: IntermediateRefractionRequest | null;
  note: string | null;
}

export interface ObjectiveRefractionRequest {
  leftEye: ObjectiveRefractionEyeRequest | null;
  rightEye: ObjectiveRefractionEyeRequest | null;
  note: string | null;
}

export interface EncounterRefractionsRequest {
  current: RefractionRequest | null;
  objective: ObjectiveRefractionRequest | null;
  subjective: RefractionRequest | null;
  final: RefractionRequest | null;
}

export interface FundusCameraEyeRequest {
  note: string | null;
  photoId?: string | null;
}

export interface FundusCameraRequest {
  instrument: FundusCameraInstrument | null;
  rightEye: FundusCameraEyeRequest | null;
  leftEye: FundusCameraEyeRequest | null;
}

export interface EncounterOcularMetricsRequest {
  note: any | null;
}

export interface UpdateEncounterExaminationRequest {
  eyeHealth: EyeHealthRequest | null;
  investigation: InvestigationRequest | null;
  supplementaryTests: EncounterSupplementaryTestsRequest | null;
  refractions: EncounterRefractionsRequest | null;
  ocularMetrics: EncounterOcularMetricsRequest | null;
}

export interface UpdateEncounterSummaryRequest {
  advice: any[] | null;
  visionStatus: any[] | null;
  eyeHealth: any[] | null;
  visionCorrection: any[] | null;
  givenPrescription: RefractionRequest | null;
  recall: number | null;
}

export interface UpdateEncounterRequest {
  id: string;
  version: string | null;
  history: Optional<UpdateEncounterHistoryRequest | null> | null;
  examination: Optional<UpdateEncounterExaminationRequest | null> | null;
  summary: Optional<UpdateEncounterSummaryRequest | null> | null;
  note: Optional<string | null> | null;
}

export const FundusCameraInstrument = enumObject('FundusCameraInstrument', ['VOLK_VIVA'] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type FundusCameraInstrument = EnumObjectValue<typeof FundusCameraInstrument>;

export const TonometryInstrument = enumObject('TonometryInstrument', [
  'ICARE',
  'GOLDMANN',
  'TONO_PEN',
] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type TonometryInstrument = EnumObjectValue<typeof TonometryInstrument>;

export const PachymetryInstrument = enumObject('PachymetryInstrument', ['OCCUITY_PM1'] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PachymetryInstrument = EnumObjectValue<typeof PachymetryInstrument>;

export const VisualFieldInstrument = enumObject('VisualFieldInstrument', ['MRF'] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type VisualFieldInstrument = EnumObjectValue<typeof VisualFieldInstrument>;

export const ColourVision = enumObject('ColourVision', [
  'NONE',
  'NORMAL_ISHIHARA',
  'DEFECTIVE_PROTAN',
  'DEFECTIVE_DEUTAN',
  'NOT_TESTED',
] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ColourVision = EnumObjectValue<typeof ColourVision>;

export const EncounterMobility = enumObject('EncounterMobility', [
  'FULL',
  'WALKING_STICK',
  'WALKING_FRAME',
  'WHEELCHAIR',
  'BED_BOUND',
] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type EncounterMobility = EnumObjectValue<typeof EncounterMobility>;

export const EncounterPosition = enumObject('EncounterPosition', ['SEATED', 'RECLINED', 'SUPINE'] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type EncounterPosition = EnumObjectValue<typeof EncounterPosition>;

export const EncounterCooperation = enumObject('EncounterCooperation', [
  'COOPERATIVE',
  'PARTIALLY_COOPERATIVE',
  'UNCOOPERATIVE',
] as const);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type EncounterCooperation = EnumObjectValue<typeof EncounterCooperation>;

// TODO: Update values when client confirms
export const OcularExaminationMethod = enumObject('OcularExaminationMethod', [] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type OcularExaminationMethod = EnumObjectValue<typeof OcularExaminationMethod>;

// TODO: Update values when client confirms
export const MydriaticAgent = enumObject('MydriaticAgent', [] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type MydriaticAgent = EnumObjectValue<typeof MydriaticAgent>;

export function isEncounterEditable(encounter: Encounter, userId: string, userRoles: string[]) {
  return (encounter.providerId === userId || userRoles.includes(Role.PERFORMER)) && !encounter.disabled;
}

export type EncounterReport = NonNullable<GetEncounterReportQuery['encounter']>;

export type Encounter = NonNullable<GetEncounterQuery['encounter']> & {
  glaucomaTests: GlaucomaTests | null;
  history: EncounterHistory | null;
  examination: EncounterExamination | null;
  summary: EncounterSummary | null;
};

export type EncounterOverview = NonNullable<GetEncounterOverviewQuery['encounter']>;

export type PreviousEncounter = Omit<PreviousEncounterFragment, 'history' | 'examination' | 'summary'> & {
  history: EncounterHistory | null;
  examination: EncounterExamination | null;
  summary: EncounterSummary | null;
};

export type EncounterPreviousEncounter = Omit<
  NonNullable<GetPreviousEncounterQuery['encounter']>,
  'previousEncounter'
> & {
  previousEncounter: PreviousEncounter | null;
};

export type EncounterListItem = EncounterListItemFragment;

export type EncounterReportListItem = EncounterReportListItemFragment & {
  summary: EncounterSummary | null;
  note: string | null;
};

export type PastEncounterSummary = PastEncounterSummaryFragment;

export interface RecognizeSoapNotesRequest {
  meetingTranscription: string | null;
}

export type UnbilledEncounterListItem = UnbilledEncounterListItemFragment;
export type EncounterExaminationHistoryItem = EncounterExaminationHistoryItemFragment;
export type EncounterSummaryHistoryItem = EncounterSummaryHistoryItemFragment;
